import { useMemo } from "react";
import styled from "styled-components";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ComposedChart,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { formatCurrency } from "../../../Funcoes/formatCurrency";

const ChartScroll = styled.div`
  width: 100%;
  height: 280px;
  min-height: 280px;
`;

const EmptyText = styled.p`
  margin: 0;
  font-size: 13px;
  color: #2e7d52;
`;

const BAR_COLOR = "#4a6fa5";
const LINE_COLOR = "#4a6fa5";
const ACUM_COLOR = "#b45309";

const formatAxis = (value) => {
  const num = Number(value) || 0;
  if (Math.abs(num) >= 1_000_000) {
    return `${(num / 1_000_000).toLocaleString("pt-BR", {
      maximumFractionDigits: 1,
    })}M`;
  }
  if (Math.abs(num) >= 1_000) {
    return `${(num / 1_000).toLocaleString("pt-BR", {
      maximumFractionDigits: 0,
    })}k`;
  }
  return num.toLocaleString("pt-BR");
};

/**
 * Curva de desembolso (barras / linha + acumulado) com clique por período.
 */
const DesembolsoChart = ({
  rows,
  labelKey,
  chartTipo = "bar",
  chartAcum = false,
  onSelectPeriodo,
}) => {
  const data = useMemo(() => {
    const list = Array.isArray(rows) ? rows : [];
    let soma = 0;
    return list.map((row) => {
      const valor = Number(row.valor) || 0;
      soma += valor;
      return {
        ...row,
        label: row[labelKey],
        valor,
        acumulado: Math.round(soma * 100) / 100,
      };
    });
  }, [rows, labelKey]);

  if (data.length === 0) {
    return <EmptyText>Sem dados para exibir.</EmptyText>;
  }

  const handleClick = (payload) => {
    if (!payload || !onSelectPeriodo) return;
    const { label, acumulado, ...rest } = payload;
    onSelectPeriodo(rest);
  };

  const tooltipStyle = {
    background: "var(--ctrl-surface, #fff)",
    border: "1px solid var(--ctrl-border, #e2e8f0)",
    borderRadius: 8,
    fontSize: 12,
    color: "var(--ctrl-text, #0f172a)",
  };

  const tickFill = "var(--ctrl-text-muted, #64748b)";
  const gridStroke = "rgba(100,116,139,0.2)";

  const commonAxis = (
    <>
      <CartesianGrid stroke={gridStroke} strokeDasharray="3 3" vertical={false} />
      <XAxis
        dataKey="label"
        tick={{ fill: tickFill, fontSize: 11 }}
        axisLine={{ stroke: gridStroke }}
        tickLine={false}
        interval="preserveStartEnd"
        minTickGap={24}
      />
      <YAxis
        yAxisId="valor"
        tick={{ fill: tickFill, fontSize: 11 }}
        axisLine={false}
        tickLine={false}
        width={52}
        tickFormatter={formatAxis}
      />
      {chartAcum && (
        <YAxis
          yAxisId="acum"
          orientation="right"
          tick={{ fill: ACUM_COLOR, fontSize: 11 }}
          axisLine={false}
          tickLine={false}
          width={52}
          tickFormatter={formatAxis}
        />
      )}
      <Tooltip
        formatter={(value, name) => [
          `R$ ${formatCurrency(value)}`,
          name === "acumulado" ? "Acumulado" : "Previsto",
        ]}
        labelFormatter={(label) => label}
        contentStyle={tooltipStyle}
        cursor={{ fill: "rgba(74, 111, 165, 0.08)" }}
      />
    </>
  );

  const ChartTag =
    chartTipo === "line"
      ? chartAcum
        ? ComposedChart
        : LineChart
      : chartAcum
        ? ComposedChart
        : BarChart;

  return (
    <ChartScroll>
      <ResponsiveContainer width="100%" height="100%">
        <ChartTag
          data={data}
          margin={{ top: 12, right: chartAcum ? 12 : 8, left: 0, bottom: 4 }}
        >
          {commonAxis}

          {chartTipo === "bar" && (
            <Bar
              yAxisId="valor"
              dataKey="valor"
              fill={BAR_COLOR}
              radius={[4, 4, 0, 0]}
              maxBarSize={40}
              cursor="pointer"
              onClick={(item) => handleClick(item?.payload)}
            />
          )}

          {chartTipo === "line" && (
            <Line
              yAxisId="valor"
              type="monotone"
              dataKey="valor"
              stroke={LINE_COLOR}
              strokeWidth={2}
              dot={{
                r: 4,
                fill: LINE_COLOR,
                cursor: "pointer",
                onClick: (_e, item) => handleClick(item?.payload),
              }}
              activeDot={{
                r: 6,
                cursor: "pointer",
                onClick: (_e, item) => handleClick(item?.payload),
              }}
            />
          )}

          {chartAcum && (
            <Line
              yAxisId="acum"
              type="monotone"
              dataKey="acumulado"
              stroke={ACUM_COLOR}
              strokeWidth={2}
              strokeDasharray="4 3"
              dot={{ r: 2.5, fill: ACUM_COLOR }}
              legendType="plainline"
            />
          )}
        </ChartTag>
      </ResponsiveContainer>
    </ChartScroll>
  );
};

export default DesembolsoChart;
